/**
 * Leaderboard — HUD element (extracted from PixiHUDRenderer.js in Phase 3D).
 *
 * create(hud) builds the PIXI objects into hud.elements.leaderboard,
 * position(hud) lays them out for hud.screenWidth/Height, update(hud, …) redraws
 * from game state. `hud` is the PixiHUDRenderer coordinator: it owns
 * hudContainer, elements, screen size, _createText() and send().
 *
 * This is a popup, not a resident panel: it sits in the top-left over the kill
 * feed and only appears while gameState.leaderboardOpen is set (L key, see
 * InputManager). Game modes that have no scoreboard suppress it entirely.
 */
import * as PIXI from 'pixi.js';

/**
 * Create leaderboard display
 */
export function create(hud) {
  const container = new PIXI.Container();
  container.visible = false; // Hidden until the player opens it with L
  container.zIndex = 60;     // popup: draws over the kill feed it overlaps
  hud.hudContainer.addChild(container);
  hud.hudContainer.sortableChildren = true;

  // Background panel
  const background = new PIXI.Graphics();
  container.addChild(background);

  // Title
  const titleText = hud._createText('TOP PLAYERS', 14, 0xffffff, true);
  titleText.anchor.set(0.5, 0);
  container.addChild(titleText);

  // Pre-create entry containers (max 3 entries)
  const entries = [];
  for (let i = 0; i < 3; i++) {
    const entryContainer = new PIXI.Container();
    entryContainer.visible = false;
    container.addChild(entryContainer);

    // Rank badge background
    const rankBadge = new PIXI.Graphics();
    entryContainer.addChild(rankBadge);

    // Rank number
    const rankText = hud._createText(`${i + 1}`, 12, 0xffffff, true);
    rankText.anchor.set(0.5, 0.5);
    entryContainer.addChild(rankText);

    // Player name
    const nameText = hud._createText('', 13, 0xffffff);
    entryContainer.addChild(nameText);

    // Kill count
    const killsText = hud._createText('0', 13, 0xffff00, true);
    killsText.anchor.set(1, 0);
    entryContainer.addChild(killsText);

    entries.push({
      container: entryContainer,
      rankBadge,
      rankText,
      nameText,
      killsText
    });
  }

  hud.elements.leaderboard = { container, background, titleText, entries };
  hud._positionLeaderboard();
}

/**
 * Position leaderboard in top-left corner
 */
export function position(hud) {
  const lb = hud.elements.leaderboard;
  if (!lb) return;

  const panelWidth = 180;
  const entryHeight = 28;
  const titleHeight = 25;
  const padding = 10;
  const panelHeight = titleHeight + (entryHeight * 3) + padding * 2;

  // Top-left corner, the spot the minimap vacated.
  const margin = 30;

  lb.container.x = margin;
  lb.container.y = margin;

  // Background
  lb.background.clear();
  lb.background.roundRect(0, 0, panelWidth, panelHeight, 5);
  lb.background.fill({ color: 0x000000, alpha: 0.675 });

  // Title
  lb.titleText.x = panelWidth / 2;
  lb.titleText.y = padding;

  // Position entries
  lb.entries.forEach((entry, index) => {
    const entryY = titleHeight + padding + (index * entryHeight);

    entry.container.x = padding;
    entry.container.y = entryY;

    // Rank badge
    const rankColors = [0xffd700, 0xc0c0c0, 0xcd7f32]; // Gold, Silver, Bronze
    entry.rankBadge.clear();
    entry.rankBadge.circle(10, entryHeight / 2, 10);
    entry.rankBadge.fill({ color: rankColors[index] || 0x666666 });

    // Rank text
    entry.rankText.x = 10;
    entry.rankText.y = entryHeight / 2;
    entry.rankText.style.fill = index === 0 ? 0x000000 : 0xffffff;

    // Name
    entry.nameText.x = 28;
    entry.nameText.y = (entryHeight - 13) / 2;

    // Kills
    entry.killsText.x = panelWidth - padding * 2;
    entry.killsText.y = (entryHeight - 13) / 2;
  });

  // Store dimensions
  lb.panelWidth = panelWidth;
  lb.entryHeight = entryHeight;
}

/**
 * Update leaderboard
 * @param {Object} gameState - Game state
 * @param {Object} callbacks - Callback functions
 */
export function update(hud, gameState, callbacks) {
  const lb = hud.elements.leaderboard;
  if (!lb) return;

  const { gameModeConfig } = callbacks;

  // Two gates: the mode has to have a scoreboard at all, and the player has to
  // have asked for it with L.
  const showLeaderboard = (gameModeConfig?.showLeaderboard ?? false) && Boolean(gameState.leaderboardOpen);
  lb.container.visible = showLeaderboard;

  if (!showLeaderboard) return;

  // Team modes: title becomes the team score line, names are team-coloured
  const teams = gameState.teams;
  const teamScores = gameState.modeState?.teamScores;
  if (teams && Array.isArray(teamScores)) {
    lb.titleText.text = teams.map((t, i) => `${t.name} ${teamScores[i] ?? 0}`).join('   ');
  } else if (lb.titleText.text !== 'TOP PLAYERS') {
    lb.titleText.text = 'TOP PLAYERS';
  }

  const playerScores = typeof gameState.getScoreboardEntries === 'function'
    ? gameState.getScoreboardEntries()
    : [];

  if (!playerScores.length) {
    lb.entries.forEach(entry => { entry.container.visible = false; });
    return;
  }

  playerScores.sort((a, b) => {
    if (b.kills !== a.kills) return b.kills - a.kills;
    const nameA = a.name || 'Player';
    const nameB = b.name || 'Player';
    return nameA.localeCompare(nameB);
  });

  // Update entries (top 3)
  lb.entries.forEach((entry, index) => {
    if (index < playerScores.length) {
      const playerData = playerScores[index];
      entry.container.visible = true;

      // Truncate name if too long
      let displayName = playerData.name || 'Player';
      if (displayName.length > 12) {
        displayName = displayName.substring(0, 11) + '…';
      }

      entry.nameText.text = displayName;
      entry.killsText.text = playerData.kills.toString();

      // Colour: team colour when teams are in play; cyan for the local player otherwise
      const teamFill = typeof gameState.teamColor === 'function' ? gameState.teamColor(playerData.team) : null;
      entry.nameText.style.fill = teamFill ?? (playerData.isLocal ? 0x00ffff : 0xffffff);
    } else {
      entry.container.visible = false;
    }
  });
}
